// ============================================
// External Functions — replaces VariableManager external bindings
// Binds Ink EXTERNAL functions to JS implementations
// ============================================

import { variableManager } from './variables.js';
import { Stats } from './constants.js';
import { isPat } from './pgate.js';
import { inventoryManager } from '../systems/inventory.js';

// Viewer name pool for streaming get_viewer_name()
export const VIEWER_NAMES = [
    'PinkCloud99', 'DarkStar42', 'NeonKitty', 'SilverFox_', 'xXShadowXx',
    'CherryBomb88', 'MidnightWolf', 'CozyGamer', 'PixelPrincess', 'ThunderBolt7',
    'LunarMoth', 'VelvetRose', 'IceDragon', 'GoldenHour_', 'CrypticOwl',
    'BlazingPhoenix', 'SweetChaos', 'RetroVibes', 'CosmicDust', 'WildCard_',
    'NeonDreamer', 'StarlitPath', 'ShadowPup', 'RubyGlow', 'FrostByte_',
    'TwilightZone', 'ElectricSoul', 'VaporWave99', 'LovelyDove', 'StormChaser',
    'PatronAngel_X', 'PatronAngel_K', 'PatronAngel_M',
];

/**
 * F29: Stat-tier multipliers are sourced from assets/data/stat-tiers.json
 * so Ink (Globals.ink fem_gain/sub_gain helpers) and JS (this module)
 * have a single declarative source. These in-JS defaults match the JSON
 * and act as a bootstrap fallback if the JSON load races this module.
 *
 * The JSON schema also carries trait modifiers and shame tier data for
 * future consumers. Balance changes should edit the JSON first, then
 * mirror into Globals.ink fem_gain() / sub_gain() tier branches.
 */
const FEM_TIER_MULTIPLIERS = [0.85, 0.6, 0.5, 0.25];
const SUB_TIER_MULTIPLIERS = [0.85, 0.6, 0.5, 0.25];
const TIER_BREAKPOINTS     = [25, 50, 75];  // shared across Fem + Sub

// Optional: late-load the JSON so future balance changes can be picked up
// without an esbuild rebuild. Non-blocking — if fetch fails we keep the
// literals above.
if (typeof fetch !== 'undefined' && typeof window !== 'undefined') {
    fetch('assets/data/stat-tiers.json', { cache: 'no-cache' })
        .then(r => r.ok ? r.json() : null)
        .then(data => {
            if (!data) return;
            const f = data.femininity, s = data.submissiveness;
            if (f?.tierMultipliers?.length === 4) {
                FEM_TIER_MULTIPLIERS.splice(0, 4, ...f.tierMultipliers);
            }
            if (s?.tierMultipliers?.length === 4) {
                SUB_TIER_MULTIPLIERS.splice(0, 4, ...s.tierMultipliers);
            }
            // Breakpoints would need a shared reference — left for future use.
        })
        .catch(e => console.warn('[externals] stat-tiers.json load failed:', e));
}

/** Tier index 0-3 from stat value, using shared breakpoints. */
function getTier(value) {
    for (let i = 0; i < TIER_BREAKPOINTS.length; i++) {
        if (value < TIER_BREAKPOINTS[i]) return i;
    }
    return TIER_BREAKPOINTS.length;
}

/**
 * Pacing 2.0 per-action gain multiplier from the story-length slider
 * (pacing_target_days). Longer run → smaller per-action gain so the fem/sub
 * curve spreads across the run instead of maxing out early and waiting.
 * Curve = 24 / target, clamped [0.35, 1.0]. Values: 35 → 0.69×, 60 → 0.40×,
 * 90 → 0.35× (floor). Sim-tuned (tools sim: Day-to-Fem-75 lands ~0.7-0.9× of
 * the endgame door for a casual player, earlier for an active one; Fem 75 stays
 * reachable at every setting — no soft-lock). The action-cadence assumption is a
 * model; recalibrate against real playtest telemetry before final ship.
 * See PACING_RETUNE.md §4.
 */
function pacingGainMult(story) {
    let target = 60;
    try {
        const t = story.variablesState['pacing_target_days'];
        if (typeof t === 'number' && t > 0) target = t;
    } catch (e) { /* default 60 */ }
    return Math.max(0.35, Math.min(1.0, 24 / target));
}

/**
 * Bind all Ink external functions to the story instance.
 * Must be called after story creation, before first Continue().
 * @param {object} story - inkjs Story instance
 */
export function bindExternals(story) {
    // ------------------------------------------------------------------
    // is_patreon() — content gating
    // ------------------------------------------------------------------
    story.BindExternalFunction('is_patreon', () => isPat());

    // ------------------------------------------------------------------
    // get_fem_gain(baseGain) — femininity diminishing returns
    // ------------------------------------------------------------------
    story.BindExternalFunction('get_fem_gain', (baseGain) => {
        if (baseGain === 0) return 0;

        const currentFem = variableManager.getValue(Stats.FEMININITY);
        const tier = getTier(currentFem);
        // Early-game smooth ramp (v0.2.7 audit C6 smoothing): pre-fix had a
        // binary cutoff Fem<10 → 0.5, Fem>=10 → 0.85 which produced a sudden
        // doubled-speed feeling on Day 9-10. Now ramps 0.5/0.65/0.75/0.80
        // across Fem 0-25 before the tier system kicks in. fem_tier remains
        // the 0-3 Ink mapping.
        const earlyMul = currentFem < 10 ? 0.5
                       : currentFem < 15 ? 0.65
                       : currentFem < 20 ? 0.75
                       : currentFem < 25 ? 0.80
                       : null;
        const multiplier = earlyMul !== null ? earlyMul : FEM_TIER_MULTIPLIERS[tier];
        let gain = baseGain * multiplier;

        // Curious trait: +15% fem gain
        if (variableManager.hasTrait('curious')) gain *= 1.15;

        // Devot trait: -10% fem gain
        if (variableManager.hasTrait('devot')) gain *= 0.90;

        // Hardship: Slow Burn — -25% fem gain (kill-switch gated).
        try {
            if (story.variablesState['hardships_enabled'] && story.variablesState['perk_slow_burn']) gain *= 0.75;
        } catch(e) { /* vars may not exist */ }

        // Resistance multipliers (Gym-Bro / Military / Masc-Route), combined
        // into one factor and floor-capped so stacking can't trivialize
        // fem-gain to ~0 (MASC_ROUTE §11). Pre-0.2.8-identical off-route
        // chain: gym alone 0.5, military alone 0.4 (mutually exclusive
        // backgrounds), both >= the 0.3 floor so the cap never bites on legacy.
        let resistanceMul = 1.0;
        try {
            const gymDays = story.variablesState['gym_bro_resistance_days'];
            if (typeof gymDays === 'number' && gymDays > 0) resistanceMul *= 0.5;
        } catch(e) { /* var may not exist */ }
        try {
            const milDays = story.variablesState['military_resistance_days'];
            if (typeof milDays === 'number' && milDays > 0) resistanceMul *= 0.4;
        } catch(e) { /* var may not exist */ }
        // Masculine Route Gentle lever (MASC_ROUTE §2): halve fem gain while
        // committed to the masc path. Earned — gate on actually being on-route
        // so abandoning the route drops the discount. (Kill-switch stripped
        // 2026-07-07; masc_path carries the route.)
        try {
            const mascPath = story.variablesState['masc_path'];
            if (typeof mascPath === 'number' && mascPath >= 1) resistanceMul *= 0.5;
        } catch(e) { /* vars may not exist */ }
        // Floor the COMBINED resistance so a background + the masc route still
        // leaves a meaningful fem-gain (no hollowing the core loop).
        if (resistanceMul < 0.3) resistanceMul = 0.3;
        gain *= resistanceMul;

        // Pacing 2.0 (PACING_RETUNE.md §2.5): scale per-action gain by the
        // story-length slider so the stat curve lands near the endgame door.
        gain *= pacingGainMult(story);

        // Sync fem_tier to Ink (based on base Fem, not buffed total)
        try { story.variablesState['fem_tier'] = tier; } catch(e) { /* ignore */ }

        // Return the FRACTIONAL gain (no +1 floor, no rounding). The Ink
        // fem_gain wrapper banks it in fem_accum and ticks Femininity by whole
        // units, so a sub-1 gain accumulates instead of flooring to +1 (which
        // used to defeat any <1x slider multiplier). The old floor's purpose —
        // "a positive gain must never vanish" — is now served by the accumulator.
        return Math.max(0, gain);
    });

    // ------------------------------------------------------------------
    // get_sub_gain(baseGain) — submissiveness diminishing returns
    // ------------------------------------------------------------------
    story.BindExternalFunction('get_sub_gain', (baseGain) => {
        if (baseGain === 0) return 0;

        const currentSub = variableManager.getValue(Stats.SUBMISSIVENESS);
        const tier = getTier(currentSub);
        // Mirror of Fem smooth ramp (v0.2.7 audit C6 smoothing): same
        // 0.5/0.65/0.75/0.80 over Sub 0-25 so Day 1-3 Bar/Master scenes
        // don't stack to +6 Sub on night one. sub_tier remains the 0-3
        // Ink mapping.
        const earlyMul = currentSub < 10 ? 0.5
                       : currentSub < 15 ? 0.65
                       : currentSub < 20 ? 0.75
                       : currentSub < 25 ? 0.80
                       : null;
        const multiplier = earlyMul !== null ? earlyMul : SUB_TIER_MULTIPLIERS[tier];
        let gain = baseGain * multiplier;

        // Stubborn trait: -20% sub gain
        if (variableManager.hasTrait('stubborn')) gain *= 0.80;
        // Devot trait: +25% sub gain (mutually exclusive with stubborn)
        else if (variableManager.hasTrait('devot')) gain *= 1.25;
        // People Pleaser trait: +15% sub gain (mutually exclusive with stubborn)
        else if (variableManager.hasTrait('pleaser')) gain *= 1.15;

        // Hardship: Slow Burn — -20% sub gain (kill-switch gated).
        try {
            if (story.variablesState['hardships_enabled'] && story.variablesState['perk_slow_burn']) gain *= 0.80;
        } catch(e) { /* vars may not exist */ }

        // Pacing 2.0 slider multiplier (mirrors get_fem_gain).
        gain *= pacingGainMult(story);

        // Sync sub_tier to Ink
        try { story.variablesState['sub_tier'] = tier; } catch(e) { /* ignore */ }

        // Fractional — banked by the Ink sub_gain wrapper in sub_accum.
        return Math.max(0, gain);
    });

    // ------------------------------------------------------------------
    // get_allure() — total buffed Allure for Ink content checks
    // ------------------------------------------------------------------
    story.BindExternalFunction('get_allure', () => {
        let allure = variableManager.buffedVar.get('Allure') || 0;
        // Vain trait: +1 permanent Allure bonus
        if (variableManager.hasTrait('vain')) allure += 1;
        return allure;
    });

    // ------------------------------------------------------------------
    // get_comfort() — total buffed Comfort for Ink content checks
    // ------------------------------------------------------------------
    story.BindExternalFunction('get_comfort', () => {
        return Math.max(0, variableManager.buffedVar.get('Comfort') || 0);
    });

    // ------------------------------------------------------------------
    // has_trait(traitId) — personality trait check
    // ------------------------------------------------------------------
    story.BindExternalFunction('has_trait', (traitId) => {
        return variableManager.hasTrait(traitId);
    });

    // ------------------------------------------------------------------
    // get_background() — player background id
    // ------------------------------------------------------------------
    story.BindExternalFunction('get_background', () => {
        try {
            return story.variablesState['player_background'] || 0;
        } catch(e) { return 0; }
    });

    // ------------------------------------------------------------------
    // get_viewer_name() — random viewer name for streaming
    // ------------------------------------------------------------------
    story.BindExternalFunction('get_viewer_name', () => {
        const idx = Math.floor(Math.random() * VIEWER_NAMES.length);
        return VIEWER_NAMES[idx];
    });

    // ------------------------------------------------------------------
    // shame_gain(baseGain) — shame with trait modifiers + diminishing returns
    // Exhibitionist halves, Vain doubles. DR tiers at 30/60/80.
    // ------------------------------------------------------------------
    story.BindExternalFunction('shame_gain', (baseGain) => {
        if (baseGain === 0) return 0;

        let currentShame = 0;
        try { currentShame = story.variablesState['Shame'] || 0; } catch(e) { /* ignore */ }

        // Negative input = designed shame relief (breathe, spa pamper, etc).
        // Short-circuit traits + diminishing returns; apply the relief as-is
        // so Exhibitionist doesn't double it and tier DR doesn't invert the curve.
        if (baseGain < 0) {
            const nextShame = Math.max(0, currentShame + baseGain);
            try { story.variablesState['Shame'] = nextShame; } catch(e) { /* ignore */ }
            return baseGain;
        }

        let amount = getEffectiveShameGain(baseGain);

        // Diminishing returns based on current Shame tier
        // T0 (0-29): full, T1 (30-59): 70%, T2 (60-79): 40%, T3 (80+): 25%
        // 2026-05-07: Math.floor → Math.round to match get_fem_gain/get_sub_gain
        // (which round). Floor occasionally clipped 0.49 → 0; min-1 clamp below
        // saved us, but Round is safer + symmetric with sibling functions.
        if (currentShame >= 80) {
            amount = Math.round((amount * 25) / 100);
        } else if (currentShame >= 60) {
            amount = Math.round((amount * 40) / 100);
        } else if (currentShame >= 30) {
            amount = Math.round((amount * 70) / 100);
        }

        // Minimum 1 if base > 0
        if (amount < 1 && baseGain > 0) amount = 1;

        // Apply shame gain
        try { story.variablesState['Shame'] = currentShame + amount; } catch(e) { /* ignore */ }

        return amount;
    });

    // ------------------------------------------------------------------
    // refresh_equipment() — force EquippedFeminine / wearing_* recompute.
    // Runs synchronously during Continue(), so Ink sees the corrected flags
    // in the same knot. Recovers from save-desync where the inventory has
    // the right gear but the Ink VAR was stuck at a stale value.
    // Returns 0 (unused — call via `~ refresh_equipment()`).
    // ------------------------------------------------------------------
    story.BindExternalFunction('refresh_equipment', () => {
        try { inventoryManager._recalculateAllFlags(); } catch(e) {
            console.warn('[External] refresh_equipment:', e?.message);
        }
        return 0;
    });

    // ------------------------------------------------------------------
    // force_equip_feminine_outfit() — auto-equip the highest-tier FEMININE_UPPER
    // currently owned. Called from Master.ink Inspection_Punishment ptype 3
    // ("Wear this. All day. Outside.") so the order has actual teeth instead
    // of being narration-only. Returns 1 if equipped, 0 if no feminine top
    // owned. Foundby:The Cook 2026-05-13.
    // Tier order matches FEMININE_UPPER_TAGS in inventory.js, descending by
    // dress-code intensity (latex_dress > maid > schoolgirl > ... > crop_jacket).
    // ------------------------------------------------------------------
    const FEMININE_UPPER_PRIORITY = [
        'latex_dress', 'maid_outfit', 'schoolgirl_uniform', 'bunny_suit',
        'bar_dress', 'cafe_maid_uniform', 'club_uniform',
        'dress', 'sundress', 'nightie',
        'blouse', 'crop_top', 'oversized_sweater', 'crop_jacket',
    ];
    story.BindExternalFunction('force_equip_feminine_outfit', () => {
        try {
            for (const tag of FEMININE_UPPER_PRIORITY) {
                if (inventoryManager.ownsItem(tag)) {
                    inventoryManager.equipByTag(tag);
                    return 1;
                }
            }
            return 0;
        } catch(e) {
            console.warn('[External] force_equip_feminine_outfit:', e?.message);
            return 0;
        }
    });

    // ------------------------------------------------------------------
    // assert_state_consistent() — read-only invariant scan, called from
    // overnight_processing once per night. Detects impossible state
    // combinations (master leaks, equipment desync, stat overflow) and
    // emits one telemetry event per unique violation per session.
    //
    // Bug class history:
    //  - v0.2.4.2 master_intensity-leak (Sarah-Rework): Master content
    //    fired on Independent path because gates checked MasterRelationship
    //    instead of master_intensity.
    //  - v0.2.3.5 EquippedFeminine race: items.json flag got into the
    //    inventory recalculation cycle, breaking unrelated items.
    //  - chastity flag/cage_tier desync after partial saves.
    //
    // Read-only: never mutates state. Returns 0.
    // ------------------------------------------------------------------
    const _seenViolations = new Set();
    function _emitStateViolation(key, detail) {
        if (_seenViolations.has(key)) return;
        _seenViolations.add(key);
        const record = { key, detail, ts: Date.now() };
        console.warn(`[StateValidator] ${key}: ${JSON.stringify(detail)}`);
        try {
            if (!window.__stateViolations) window.__stateViolations = [];
            if (window.__stateViolations.length < 100) window.__stateViolations.push(record);
        } catch(e) {}
        try { window.dispatchEvent(new CustomEvent('state-violation', { detail: record })); } catch(e) {}
    }

    story.BindExternalFunction('assert_state_consistent', () => {
        try {
            const v = (k) => {
                try { return story.variablesState[k]; } catch(e) { return undefined; }
            };
            const num = (k) => Number(v(k) || 0);
            const flag = (k) => !!v(k);

            // 1. Master leak — Independent run with Master state. The single
            //    most important check (Sarah-Rework v0.2.4.2 entire bug class).
            const intensity = num('master_intensity');
            const masterRel = num('MasterRelationship');
            const masterRank = num('master_rank');
            if (intensity === 0) {
                if (masterRank > 0) {
                    _emitStateViolation('master_leak_rank', { master_rank: masterRank, master_intensity: 0 });
                }
                if (masterRel > 5) {
                    _emitStateViolation('master_leak_relationship', { MasterRelationship: masterRel, master_intensity: 0 });
                }
                if (flag('master_collared')) {
                    _emitStateViolation('master_leak_collared', { master_collared: true, master_intensity: 0 });
                }
                // Independent path uses flat $200 auto-deduct; RentAmount default 150
                // and never escalates on this branch. If we observe RentAmount > 200,
                // it means a Roommate/Landlord/Owner save was downgraded to 0 mid-run
                // OR the editor failed to set master_intensity at game start. Either
                // case: surface to telemetry so a future repro lands with diagnostics.
                // Foundby:VPS player 0.2.5.8 ("chose Independent, got landlord with rent escalating").
                const rent = num('RentAmount');
                if (rent > 200) {
                    _emitStateViolation('master_leak_rent_escalated', { RentAmount: rent, master_intensity: 0 });
                }
            }

            // 2. Chastity desync — flag without cage tier (or vice versa).
            //    EXCEPTION: the chastity BELT (chastity_form === 'belt',
            //    KITTEN_CHROME §4b) legitimately sets wearing_chastity=true with
            //    cage_tier=0 — a belt is a tier-less Front device, only cages set
            //    cage_tier (Globals.ink:155, FetishStore.ink:97). Without this
            //    guard the check fires a false positive for every belt-wearer
            //    (foundby:VPS 0.2.8.1, Chastity_Belt_FirstNight) — the same flaw
            //    that retired the HRT desync check at #3 below.
            const wearingCage = flag('wearing_chastity');
            const cageTier = num('cage_tier');
            const chastityForm = v('chastity_form');
            if (wearingCage && cageTier === 0 && chastityForm !== 'belt') {
                _emitStateViolation('chastity_flag_no_tier', { wearing_chastity: true, cage_tier: 0, chastity_form: chastityForm });
            }

            // 3. (removed) HRT desync check — referenced non-existent VAR
            //    `hrt_session_count` (actual counter is `hrt_injections`) AND the
            //    intended invariant was wrong: `taking_hormones=true` is set by
            //    the oral Hormone Supplements pharmacy item, while `hrt_injections`
            //    only ticks for clinic injections. Oral-pills-only is a legitimate
            //    route, and Clinic.ink:897 already narrates that crossover. The
            //    check fired as a false positive for every Hormone Supplements
            //    buyer who hadn't been to the clinic. Removed 2026-05-17.

            // 4. Stat range violations. Base stats are capped at 100 by VariableManager
            //    but Ink sees buffed values that CAN exceed 100. We only flag when
            //    the BASE itself is implausible (negative or huge). The Fem cap
            //    lifts to 200 once fully_transformed (fem_cap() / _capFor) — a
            //    hardcoded 110 here false-positived on every capstone player
            //    (misread as real drift in VPS report triage 2026-07-17).
            //    variableManager.baseVar() returns the un-buffed base.
            try {
                const fem = variableManager.baseVar?.get?.(Stats.FEMININITY);
                const sub = variableManager.baseVar?.get?.(Stats.SUBMISSIVENESS);
                const sha = variableManager.baseVar?.get?.(Stats.SHAME);
                const femCap = flag('fully_transformed') ? 200 : 100;
                if (typeof fem === 'number' && (fem < 0 || fem > femCap + 10)) {
                    _emitStateViolation('fem_base_out_of_range', { Femininity_base: fem });
                }
                if (typeof sub === 'number' && (sub < 0 || sub > 110)) {
                    _emitStateViolation('sub_base_out_of_range', { Submissiveness_base: sub });
                }
                if (typeof sha === 'number' && (sha < 0 || sha > 110)) {
                    _emitStateViolation('shame_base_out_of_range', { Shame_base: sha });
                }
            } catch(e) {}

            // 5. Energy overshoot — Energy should never exceed max_energy + small slack.
            const energy = num('Energy');
            const maxEnergy = num('max_energy') || 100;
            if (energy > maxEnergy + 10) {
                _emitStateViolation('energy_overshoot', { Energy: energy, max_energy: maxEnergy });
            }

            // 6. Girlfriend break-up inconsistency — broken up but relationship still positive.
            if (flag('girlfriend_broken_up') && num('sarah_relationship') > 0) {
                _emitStateViolation('girlfriend_broken_but_positive', {
                    girlfriend_broken_up: true,
                    sarah_relationship: num('sarah_relationship'),
                });
            }
        } catch(e) {
            // Silent fail: validator must never break the game.
            console.warn('[StateValidator] check failed:', e?.message);
        }
        return 0;
    });

    // ------------------------------------------------------------------
    // get_daily_summary() — build summary string from statistics
    // ------------------------------------------------------------------
    story.BindExternalFunction('get_daily_summary', () => {
        // Build a summary from Ink variables
        const day = variableManager.getValue('Day');
        const fem = variableManager.getValue(Stats.FEMININITY);
        const sub = variableManager.getValue(Stats.SUBMISSIVENESS);
        const money = variableManager.getValue(Stats.MONEY);
        return `Day ${day} | Fem: ${fem} | Sub: ${sub} | $${money}`;
    });
}

// ------------------------------------------------------------------
// Static helpers for choice preview (mirrors InkManager C# methods)
// ------------------------------------------------------------------

/**
 * 2026-05-07 Pacing-Phase-Wrapper preview helper.
 * Mirrors the Globals.ink fem_gain/sub_gain/shame_gain wrapper so choice
 * badges show the ACTUAL post-wrapper gain, not the raw #fem:N tag value.
 * Without this, a Day-3 Standard-Mode choice with #fem:3 displayed "+3"
 * but applying it returned 0 — confusing/misleading.
 *
 * mode='fem'|'shame': hard W1 lock (return 0)
 * mode='sub': milder W1 (return half)
 *
 * @param {object} story - inkjs Story instance (needed for pacing_mode read)
 * @param {number} baseGain - the gain after tier+trait multipliers
 * @param {'fem'|'sub'|'shame'} mode
 * @returns {number} gain after Phase-Wrapper
 */
function applyPacingPhaseLock(story, baseGain, mode) {
    if (!story || baseGain <= 0) return baseGain;
    let pacingMode = 1, speedSignal = false, day = 1;
    try {
        pacingMode = story.variablesState['pacing_mode'];
        speedSignal = !!story.variablesState['speed_signal_active'];
        day = story.variablesState['Day'];
        if (typeof pacingMode !== 'number') pacingMode = 1;
        if (typeof day !== 'number') day = 1;
    } catch (e) { return baseGain; }

    // Speed-signal OR Fast-Mode bypasses entirely
    if (speedSignal || pacingMode === 2) return baseGain;

    let g = baseGain;
    const half = (v) => Math.max(0, Math.round(v / 2));

    if (pacingMode === 1) {
        // Standard (5W)
        if (mode === 'fem' || mode === 'shame') {
            if (day <= 7) return 0;
            if (day <= 14) return half(g);
        } else if (mode === 'sub') {
            // Sub milder: W1 = half (not 0), W2 = 75%
            if (day <= 7) return half(g);
            if (day <= 14) return Math.max(0, Math.round(g * 0.75));
        }
    } else if (pacingMode === 0) {
        // Slow Burn (6W)
        if (mode === 'fem' || mode === 'shame') {
            if (day <= 14) return 0;
            if (day <= 21) return half(g);
        } else if (mode === 'sub') {
            if (day <= 14) return half(g);
            if (day <= 21) return Math.max(0, Math.round(g * 0.75));
        }
    } else if (pacingMode === 3) {
        // Very Slow (7W)
        if (mode === 'fem' || mode === 'shame') {
            if (day <= 21) return 0;
            if (day <= 28) return half(g);
        } else if (mode === 'sub') {
            if (day <= 21) return half(g);
            if (day <= 28) return Math.max(0, Math.round(g * 0.75));
        }
    }
    return g;
}

/**
 * Calculate effective fem gain after diminishing returns and trait modifiers.
 * Used by story manager for choice badge previews.
 * @param {number} baseGain - Raw #fem: tag value
 * @param {object} story - inkjs Story instance
 * @returns {number}
 */
export function getEffectiveFemGain(baseGain, story) {
    if (baseGain <= 0) return 0;
    // Pacing phase-lock mirrors the Ink fem_gain() wrapper ORDER: it reduces
    // the RAW integer base FIRST (W1 -> 0, W2 -> floor(base/2)), THEN runs
    // diminishing-returns. The old order (DR first, then Math.round(half))
    // over-promised: a #fem:1 choice on Standard day 8-14 previewed +1 while
    // fem_gain delivered 0 (Ink: 1/2 = 0 integer floor). foundby:VPS 0.2.8.2
    // "can't gain feminization". Floor-then-DR matches the wrapper exactly.
    const phaseBase = pacingPhaseLockFemShameBase(story, baseGain);
    if (phaseBase <= 0) return 0;

    const currentFem = variableManager.getValue(Stats.FEMININITY);
    const tier = getTier(currentFem);
    // Mirror smooth ramp from get_fem_gain (v0.2.7 audit H6 + H5)
    const earlyMul = currentFem < 10 ? 0.5
                   : currentFem < 15 ? 0.65
                   : currentFem < 20 ? 0.75
                   : currentFem < 25 ? 0.80
                   : null;
    const multiplier = earlyMul !== null ? earlyMul : FEM_TIER_MULTIPLIERS[tier];
    let gain = phaseBase * multiplier;

    if (variableManager.hasTrait('curious')) gain *= 1.15;
    if (variableManager.hasTrait('devot')) gain *= 0.90;

    try {
        if (story) {
            const gymDays = story.variablesState['gym_bro_resistance_days'];
            if (typeof gymDays === 'number' && gymDays > 0) gain *= 0.5;
            const milDays = story.variablesState['military_resistance_days'];
            if (typeof milDays === 'number' && milDays > 0) gain *= 0.4;
        }
    } catch(e) { /* ignore */ }

    // Pacing 2.0 parity: mirror get_fem_gain's slider multiplier + fractional
    // accumulator. get_fem_gain now returns a FRACTIONAL amount (no +1 floor);
    // the Ink wrapper banks it in fem_accum and ticks Femininity by whole units.
    // So the honest preview is the number of WHOLE points this action ticks NOW
    // = floor(fem_accum + this fractional gain). A 0 result means the action
    // banks progress (the bar creeps) but doesn't cross a whole point yet — the
    // badge renderer hides 0, so no misleading "+1"/"+0".
    gain *= pacingGainMult(story);
    let accum = 0;
    try {
        const a = story?.variablesState?.['fem_accum'];
        if (typeof a === 'number' && a > 0) accum = a;
    } catch (e) { /* ignore */ }
    return Math.max(0, Math.floor(accum + gain));
}

/**
 * Ink-faithful fem/shame pacing phase-lock applied to the RAW integer base.
 * Mirrors the fem_gain() + shame_gain() wrappers in Globals.ink EXACTLY:
 *   - Fast-Mode (pacing_mode 2) or speed_signal_active -> bypass (full gain).
 *   - W1 (full setup lock) -> return 0.
 *   - W2 (half phase) -> Math.floor(base / 2); 0 if that floors below 1.
 *   - W3+ -> unchanged base.
 * W1/W2 day boundaries: Standard(1) 7/14, Slow-Burn(0) 14/21, Very-Slow(3) 21/28.
 * This MUST stay in lockstep with the Ink wrappers; previously the JS preview
 * ran AFTER diminishing-returns with Math.round(half), so floor-vs-round
 * diverged and a +1 tag previewed +1 while Ink delivered 0.
 */
function pacingPhaseLockFemShameBase(story, base) {
    if (!story || base <= 0) return base;
    let pacingMode = 1, speedSignal = false, day = 1;
    try {
        pacingMode = story.variablesState['pacing_mode'];
        speedSignal = !!story.variablesState['speed_signal_active'];
        day = story.variablesState['Day'];
        if (typeof pacingMode !== 'number') pacingMode = 1;
        if (typeof day !== 'number') day = 1;
    } catch (e) { return base; }

    if (speedSignal || pacingMode === 2) return base;

    const w1 = pacingMode === 0 ? 14 : pacingMode === 3 ? 21 : 7;
    const w2 = pacingMode === 0 ? 21 : pacingMode === 3 ? 28 : 14;
    if (day <= w1) return 0;
    if (day <= w2) { const h = Math.floor(base / 2); return h < 1 ? 0 : h; }
    return base;
}

/**
 * True when a positive #fem:/#shame: tag base is FULLY zeroed by the pacing
 * phase-lock (W1 lock, or W2 floor of a +1). Used by the choice-selection
 * path to surface a one-time "transformation still settling" tip so the
 * (correctly) hidden badge doesn't read as a broken game. foundby:VPS 0.2.8.2.
 */
export function femGainPacingSuppressed(story, baseTag) {
    return baseTag > 0 && pacingPhaseLockFemShameBase(story, baseTag) <= 0;
}

/**
 * Calculate effective sub gain after diminishing returns and trait modifiers.
 * @param {number} baseGain - Raw #sub: tag value
 * @param {object} [story] - optional inkjs Story instance for pacing-phase lock
 * @returns {number}
 */
/**
 * Ink-faithful sub pacing phase-base. Mirrors the sub_gain() wrapper: MILDER
 * than fem — only ONE early window that halves the raw base (Standard D1-7,
 * Slow D1-14, Very-Slow D1-21), floored to 0 if <1, no W2 step. (The old
 * applyPacingPhaseLock('sub') wrongly added a W2 75% step the Ink never had.)
 */
function subPhaseLockBase(story, base) {
    if (!story || base <= 0) return base;
    let pacingMode = 1, speedSignal = false, day = 1;
    try {
        pacingMode = story.variablesState['pacing_mode'];
        speedSignal = !!story.variablesState['speed_signal_active'];
        day = story.variablesState['Day'];
        if (typeof pacingMode !== 'number') pacingMode = 1;
        if (typeof day !== 'number') day = 1;
    } catch (e) { return base; }
    if (speedSignal || pacingMode === 2) return base;
    const w1 = pacingMode === 0 ? 14 : pacingMode === 3 ? 21 : 7;
    if (day <= w1) { const h = Math.floor(base / 2); return h < 1 ? 0 : h; }
    return base;
}

export function getEffectiveSubGain(baseGain, story) {
    if (baseGain <= 0) return 0;
    const phaseBase = subPhaseLockBase(story, baseGain);
    if (phaseBase <= 0) return 0;

    const currentSub = variableManager.getValue(Stats.SUBMISSIVENESS);
    const tier = getTier(currentSub);
    // Mirror smooth ramp from get_sub_gain (v0.2.7 audit H6 + H5)
    const earlyMul = currentSub < 10 ? 0.5
                   : currentSub < 15 ? 0.65
                   : currentSub < 20 ? 0.75
                   : currentSub < 25 ? 0.80
                   : null;
    const multiplier = earlyMul !== null ? earlyMul : SUB_TIER_MULTIPLIERS[tier];
    let gain = phaseBase * multiplier;

    if (variableManager.hasTrait('stubborn')) gain *= 0.80;
    else if (variableManager.hasTrait('devot')) gain *= 1.25;
    else if (variableManager.hasTrait('pleaser')) gain *= 1.15;

    // Pacing 2.0: slider multiplier + fractional accumulator (mirror get_sub_gain
    // + the sub_accum bank). Preview = whole points this action ticks now.
    gain *= pacingGainMult(story);
    let accum = 0;
    try {
        const a = story?.variablesState?.['sub_accum'];
        if (typeof a === 'number' && a > 0) accum = a;
    } catch (e) { /* ignore */ }
    return Math.max(0, Math.floor(accum + gain));
}

/**
 * Calculate effective shame gain after trait modifiers.
 * Exhibitionist halves, Vain doubles.
 * @param {number} baseGain - Raw #shame: tag value
 * @returns {number}
 */
export function getEffectiveShameGain(baseGain, story) {
    if (baseGain === 0) return 0;

    // Relief (negative) bypasses the phase-lock entirely, mirroring shame_gain's
    // `base < 0` early branch in Globals.ink. Trait mods preserved as before.
    if (baseGain < 0) {
        let amount = baseGain;
        if (variableManager.hasTrait('exhibitionist')) amount = Math.floor(amount / 2);
        if (variableManager.hasTrait('vain')) amount = amount * 2;
        return amount;
    }

    // Positive: pacing phase-lock on the RAW base FIRST (Ink shame_gain ORDER),
    // then trait mods. Same floor-vs-round fix as fem — the old DR/traits-then-
    // round-half order over-promised a #shame:1 as +1 while Ink delivered 0.
    const phaseBase = pacingPhaseLockFemShameBase(story, baseGain);
    if (phaseBase <= 0) return 0;
    let amount = phaseBase;
    if (variableManager.hasTrait('exhibitionist')) {
        amount = Math.floor(amount / 2);
        if (amount < 1) amount = 1;
    }
    if (variableManager.hasTrait('vain')) {
        amount = amount * 2;
    }
    return amount;
}

/**
 * Calculate passive arousal gain from do_action().
 * @param {object} story - inkjs Story instance
 * @returns {number}
 */
export function getPassiveArousalGain(story) {
    if (!story) return 0;
    let femininity = 0, chastity = false, drunk = false;
    try { femininity = story.variablesState['Femininity'] || 0; } catch(e) {}
    try { chastity = !!story.variablesState['wearing_chastity']; } catch(e) {}
    try { drunk = !!story.variablesState['is_drunk']; } catch(e) {}

    let passive = 0;
    if (femininity >= 50) passive += 5;
    else if (femininity >= 30) passive += 4;
    else if (femininity >= 15) passive += 3;
    else if (femininity >= 5) passive += 2;

    if (chastity) passive += 2;  // mirror Ink arousal_inflow: chastity cut +3->+2 in 0.2.9
    if (drunk) passive += 5;
    if (variableManager.hasTrait('exhibitionist')) passive += 1;

    return passive;
}

/**
 * Calculate passive sub gain from do_action() (when Arousal >= 50).
 * @param {object} story - inkjs Story instance
 * @returns {number}
 */
export function getPassiveSubGain(story) {
    if (!story) return 0;
    let arousal = 0;
    try { arousal = story.variablesState['Arousal'] || 0; } catch(e) {}
    if (arousal < 50) return 0;
    return getEffectiveSubGain(1, story);
}

/**
 * Calculate arousal energy penalty for choice preview.
 * @param {object} story - inkjs Story instance
 * @returns {number}
 */
export function getArousalPenalty(story) {
    if (!story) return 0;
    let arousal = 0, hasPerk = false;
    try { arousal = story.variablesState['Arousal'] || 0; } catch(e) {}
    try { hasPerk = !!story.variablesState['perk_arousal_resist']; } catch(e) {}

    // Mirror Ink arousal_energy_penalty() exactly. 0.2.9 softened the high tiers
    // (75:8->6, 50:5->4, perk ~half; foundby:Jayjay) in Globals.ink but this
    // preview was left at the old 8/5 — the energy-cost badge over-stated the
    // charge at high arousal vs what do_action actually deducts. Re-synced.
    if (arousal >= 75) return hasPerk ? 3 : 6;
    if (arousal >= 50) return hasPerk ? 2 : 4;
    if (arousal >= 25) return hasPerk ? 1 : 2;
    return 0;
}

/**
 * Calculate comfort energy discount for choice preview.
 * @param {number} energyCost
 * @param {object} story - inkjs Story instance
 * @returns {number}
 */
export function getComfortDiscount(energyCost, story) {
    if (!story) return 0;
    let comfort = 0;
    try { comfort = story.variablesState['Comfort'] || 0; } catch(e) {}
    if (comfort <= 0) return 0;
    return Math.min(comfort, energyCost - 1);
}
